import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { protocolosKeys } from '@/features/protocolos/hooks/useProtocolos';

import { hpgApi } from '../api';
import type { Hpg, HpgCreate, HpgUpdate } from '../types';

const QUERY_KEY_BASE = ['analisis-hpg'] as const;

export const hpgKeys = {
  all: QUERY_KEY_BASE,
  byProtocolo: (uidProtocolo: string) =>
    [...QUERY_KEY_BASE, 'by-protocolo', uidProtocolo] as const,
  detail: (uidProtocolo: string, uidAnalisis: string) =>
    [...QUERY_KEY_BASE, uidProtocolo, uidAnalisis] as const,
};

export function useHpgListByProtocolo(uidProtocolo: string | undefined) {
  return useQuery({
    queryKey: uidProtocolo ? hpgKeys.byProtocolo(uidProtocolo) : hpgKeys.all,
    queryFn: ({ signal }) => hpgApi.listByProtocolo(uidProtocolo!, signal),
    enabled: !!uidProtocolo,
  });
}

export function useHpg(uidProtocolo: string | undefined, uidAnalisis: string | undefined) {
  return useQuery({
    queryKey:
      uidProtocolo && uidAnalisis ? hpgKeys.detail(uidProtocolo, uidAnalisis) : hpgKeys.all,
    queryFn: ({ signal }) => hpgApi.getById(uidProtocolo!, uidAnalisis!, signal),
    enabled: !!(uidProtocolo && uidAnalisis),
  });
}

export function useCreateHpg() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ uidProtocolo, data }: { uidProtocolo: string; data: HpgCreate }) =>
      hpgApi.create(uidProtocolo, data),
    onSuccess: (_data, { uidProtocolo }) => {
      void queryClient.invalidateQueries({ queryKey: hpgKeys.byProtocolo(uidProtocolo) });
    },
  });
}

export function useUpdateHpg() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      uidProtocolo,
      uidAnalisis,
      data,
    }: {
      uidProtocolo: string;
      uidAnalisis: string;
      data: HpgUpdate;
    }) => hpgApi.update(uidProtocolo, uidAnalisis, data),
    onSuccess: (updated: Hpg, { uidProtocolo, uidAnalisis }) => {
      void queryClient.invalidateQueries({ queryKey: hpgKeys.byProtocolo(uidProtocolo) });
      queryClient.setQueryData(hpgKeys.detail(uidProtocolo, uidAnalisis), updated);
    },
  });
}

export function useDeleteHpg() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      uidProtocolo,
      uidAnalisis,
      cascade,
    }: {
      uidProtocolo: string;
      uidAnalisis: string;
      cascade?: boolean;
    }) => hpgApi.remove(uidProtocolo, uidAnalisis, cascade),
    onSuccess: (_data, { uidProtocolo }) => {
      void queryClient.invalidateQueries({ queryKey: hpgKeys.byProtocolo(uidProtocolo) });
      void queryClient.invalidateQueries({ queryKey: protocolosKeys.detail(uidProtocolo) });
    },
  });
}
